import type { Wire } from '@map-my-money/engine';
import type { WirePlan } from './run.ts';

const sameAmount = (a: string, b: string): boolean => Number(a) === Number(b);

const enteredByBalance = (t: Wire.Transaction | undefined): t is Wire.Transaction & { debtCurrentBalance: string } =>
  t?.type === 'debt' && t.debtEntryMode === 'currentBalance' && t.debtCurrentBalance !== undefined;

/**
 * The days typed balances were true, stamped on every write. The web app applies it before it saves
 * and the server again on every write, so both hold the same document.
 *
 * The setup day (decision 7), `configuration.initialBalanceDate`: a new plan, or a changed balance
 * whose writer left the day as it was, gets `today`. An unchanged balance keeps the previous version's
 * day, and a plan that never had one keeps none: stamping today on an unrelated edit would move now
 * for a balance typed long before.
 *
 * A debt's balance day (R23, judgement call 11), `debtBalanceDate`, for a debt entered by its
 * balance: the same rule per debt, with a new debt counting as typed today (in a new plan or a copy,
 * the plan's setup day). Undated debts take the setup day, so when the setup day moves they are first
 * pinned to the previous one: retyping the cash balance never re-dates an old card balance.
 */
export function withSetupDay<P extends WirePlan>(plan: P, previous: WirePlan | null, today: string): P {
  const { initialBalance, initialBalanceDate } = plan.configuration;
  const before = previous?.configuration;
  const typedNow = before === undefined || !sameAmount(initialBalance, before.initialBalance);
  const day = typedNow
    ? initialBalanceDate === undefined || initialBalanceDate === before?.initialBalanceDate
      ? today
      : initialBalanceDate
    : (initialBalanceDate ?? before.initialBalanceDate);
  const setupDayMoved = before?.initialBalanceDate !== undefined && day !== before.initialBalanceDate;

  const balanceDay = (debt: Wire.Transaction & { debtCurrentBalance: string }): string | undefined => {
    if (previous === null) return debt.debtBalanceDate ?? day;
    const was = previous.transactions?.find((t) => t.id === debt.id);
    if (!enteredByBalance(was) || !sameAmount(debt.debtCurrentBalance, was.debtCurrentBalance)) {
      return debt.debtBalanceDate === undefined || debt.debtBalanceDate === was?.debtBalanceDate ? today : debt.debtBalanceDate;
    }
    return debt.debtBalanceDate ?? was.debtBalanceDate ?? (setupDayMoved ? before?.initialBalanceDate : undefined);
  };

  let debtsChanged = false;
  const transactions = plan.transactions?.map((t) => {
    if (!enteredByBalance(t)) return t;
    const stamped = balanceDay(t);
    if (stamped === t.debtBalanceDate || stamped === undefined) return t;
    debtsChanged = true;
    return { ...t, debtBalanceDate: stamped };
  });

  if ((day === initialBalanceDate || day === undefined) && !debtsChanged) return plan;
  return {
    ...plan,
    ...(debtsChanged ? { transactions } : {}),
    configuration: day === undefined ? plan.configuration : { ...plan.configuration, initialBalanceDate: day },
  };
}
